<template>
  <div
    class="trend-analysis"
    :class="{ 'trend-analysis--queried': hasQueried }"
    v-loading="pageLoading"
    element-loading-text="加载中..."
  >
    <div class="filter-area" :class="{ 'filter-area--mobile': isMobile }">
      <template v-if="!isMobile">
        <span class="filter-label">开始:</span>
        <el-date-picker
          v-model="dStart"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="开始日期"
          :size="isMini"
          class="filter-date"
        />
        <span class="filter-label">结束:</span>
        <el-date-picker
          v-model="dEnd"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="结束日期"
          :size="isMini"
          class="filter-date"
        />
        <el-button type="primary" :size="isMini" :loading="subPlatesLoading" @click="handleQuery">
          查询
        </el-button>
        <el-button
          v-if="!isMobile"
          type="primary"
          :size="isMini"
          :disabled="!canExport"
          :loading="exportLoading"
          @click="handleExport"
        >
          导出 Excel
        </el-button>
      </template>
      <template v-else>
        <div class="filter-row">
          <span class="filter-label">开始:</span>
          <el-date-picker
            v-model="dStart"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="开始日期"
            size="mini"
            class="filter-date"
          />
        </div>
        <div class="filter-row">
          <span class="filter-label">结束:</span>
          <el-date-picker
            v-model="dEnd"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="结束日期"
            size="mini"
            class="filter-date"
          />
          <el-button type="primary" size="mini" :loading="subPlatesLoading" @click="handleQuery">
            查询
          </el-button>
        </div>
      </template>
    </div>

    <div class="plates stocks">
      <div class="stocks-title">
        <span>大板块</span>
        <el-button type="text" size="mini" @click="addDialogVisible = true">+ 添加</el-button>
      </div>
      <div class="stocks-list" :class="{ 'stocks-list--h5': isMobile }">
        <div
          v-for="plate in plates"
          :key="plate.plateId"
          class="stock-item"
          @click="handlePlateClick(plate)"
        >
          <el-tag
            class="stock-tag"
            :effect="isPlateSelected(plate) ? 'dark' : 'plain'"
            :closable="!plate.isDefault"
            @close.stop="handleRemovePlate(plate)"
          >
            {{ plate.plateName }}
          </el-tag>
        </div>
      </div>
    </div>

    <div
      v-if="hasQueried"
      class="content-stack"
      :class="{ 'content-stack--with-drill': allConfirmedSubPlates.length }"
    >
      <div class="results-area" v-loading="subPlatesLoading" element-loading-text="加载中...">
      <div class="section-title section-title--with-action">
        子板块区间汇总
        <span class="section-sub">（{{ dStart }} ~ {{ dEnd }}）</span>
        <el-button
          type="primary"
          :size="isMini"
          :disabled="!allSelectedSubPlates.length"
          :loading="tabLoading"
          @click="handleConfirmSubPlates"
        >
          确定
        </el-button>
      </div>
      <el-tabs
        v-if="selectedPlates.length"
        v-model="activeParentTab"
        class="panel-tabs panel-tabs--fixed"
        @tab-click="handleParentTabClick"
      >
        <el-tab-pane
          v-for="parent in selectedPlates"
          :key="parent.plateId"
          :label="parent.plateName"
          :name="parent.plateId"
        >
          <div class="sub-plates-table-wrap">
            <el-table
              :ref="'subPlateTable_' + parent.plateId"
              :data="subPlatesByParent[parent.plateId] || []"
              border
              :size="isMini"
              :max-height="subPlateTableMaxHeight"
              class="even-table sub-plate-table"
              style="width: 100%"
              @row-click="(row, column) => handleSubPlateRowClick(parent, row, column)"
              @selection-change="(rows) => handleSubPlateSelectionChange(parent, rows)"
            >
              <el-table-column type="selection" width="48" />
              <el-table-column prop="plateName" label="子板块名称" />
              <el-table-column prop="intervalStrength" label="区间强度" sortable />
              <el-table-column
                prop="intervalChangePct"
                label="区间涨幅"
                sortable
                :sort-method="sortIntervalChangePct"
              />
              <el-table-column
                prop="intervalNetYi"
                label="区间净额(亿)"
                sortable
                :sort-method="sortIntervalNetYi"
              />
              <el-table-column prop="score" label="得分" sortable :formatter="formatScore" />
              <el-table-column prop="compareScore" label="比较得分" sortable :formatter="formatScore" />
            </el-table>
          </div>
          <div v-if="!(subPlatesByParent[parent.plateId] || []).length && !subPlatesLoading" class="empty-tip">
            暂无子板块数据
          </div>
        </el-tab-pane>
      </el-tabs>
      <div v-if="allSelectedSubPlates.length" class="selection-tip">
        已选 {{ allSelectedSubPlates.length }} 个子板块
      </div>
      <div v-if="!selectedPlates.length" class="empty-tip">请先选择大板块</div>
      </div>

      <div v-if="allConfirmedSubPlates.length" class="drill-area" v-loading="tabLoading" element-loading-text="加载中...">
      <div class="section-title">
        子板块：{{ selectedSubPlateNames }}
      </div>
      <el-tabs v-model="activeTab" class="panel-tabs panel-tabs--flex" @tab-click="handleDrillTabClick">
        <el-tab-pane label="每日明细" name="daily">
          <div class="daily-tab-body">
            <div v-if="dailyRows.length" class="daily-selection-tip">
              已勾选 {{ selectedDailySubPlateIds.length }} 个子板块，切换至「区间个股」查看
            </div>
            <div v-if="!dailyRows.length" class="empty-tip">暂无每日明细</div>
            <div v-else class="daily-groups-wrap">
              <el-collapse v-model="activeDailyDates" class="daily-collapse">
                <el-collapse-item
                  v-for="group in groupedDailyRows"
                  :key="group.tradeDate"
                  :title="group.tradeDate"
                  :name="group.tradeDate"
                >
                  <div class="daily-table-wrap">
                    <el-table
                      :ref="'dailyTable_' + group.tradeDate"
                      :data="group.rows"
                      border
                      :size="isMini"
                      class="even-table"
                      style="width: 100%"
                      row-key="dailyRowKey"
                      :default-sort="{ prop: 'compareScore', order: 'descending' }"
                      @selection-change="(rows) => handleDailySelectionChange(group, rows)"
                    >
                      <el-table-column type="selection" width="48" />
                      <el-table-column v-if="showSubPlateColumn" prop="plateName" label="子板块" />
                      <el-table-column v-if="showParentPlateColumn" prop="parentPlateName" label="大板块" />
                      <el-table-column prop="strength" label="强度" />
                      <el-table-column prop="rank" label="排名" />
                      <el-table-column prop="changePct" label="涨幅" />
                      <el-table-column prop="mainNetInflowYi" label="主力净额" />
                      <el-table-column prop="turnoverYi" label="成交额" />
                      <el-table-column prop="floatMktCapYi" label="流通市值" />
                      <el-table-column prop="upCount" label="上涨" />
                      <el-table-column prop="downCount" label="下跌" />
                      <el-table-column prop="flatCount" label="平盘" />
                      <el-table-column prop="turnoverRate" label="换手率" />
                      <el-table-column prop="limitUpSealYi" label="涨停封单" />
                      <el-table-column prop="limitUpCount" label="涨停家数" />
                      <el-table-column prop="largeSealYi" label="大额封单" />
                      <el-table-column prop="fundStrength" label="资金强度" :formatter="formatMetric" />
                      <el-table-column prop="fundDensity" label="资金密度" :formatter="formatMetric" />
                      <el-table-column prop="redPlateCoverage" label="红盘覆盖率" :formatter="formatMetric" />
                      <el-table-column prop="plateSentimentStrength" label="板块情绪强度" :formatter="formatMetric" />
                      <el-table-column prop="sentimentPremiumRate" label="情绪溢价率" :formatter="formatMetric" />
                      <el-table-column prop="largeSealPurity" label="大资金封单纯度" :formatter="formatMetric" />
                      <el-table-column prop="marketConsensusStrength" label="市场共识强度" :formatter="formatMetric" />
                      <el-table-column
                        prop="score"
                        label="得分"
                        sortable
                        :sort-method="sortDailyScore"
                        :formatter="formatScore"
                      />
                      <el-table-column
                        prop="compareScore"
                        label="比较得分"
                        sortable
                        :sort-method="sortDailyCompareScore"
                        :formatter="formatScore"
                      />
                    </el-table>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="区间个股" name="stocks">
          <div class="drill-stocks-tab-body">
            <div class="drill-stocks-filter">
              <el-checkbox v-model="onlyMainBoard">只看主板</el-checkbox>
              <el-checkbox v-model="filterGt80Yi">大于80亿</el-checkbox>
              <el-checkbox v-model="filterLt80Yi">小于80亿</el-checkbox>
            </div>
            <div class="drill-stocks-table-wrap" v-loading="stocksLoading" element-loading-text="加载中...">
              <el-table
                :data="showIntervalStocks"
                border
                :size="isMini"
                :max-height="drillTableMaxHeight"
                class="even-table drill-stocks-table"
                style="width: 100%"
              >
                <el-table-column prop="name" label="名称" />
                <el-table-column v-if="showIntervalSubPlateColumn" prop="subPlateName" label="子板块" />
                <el-table-column prop="plateLabel" label="所属板块" />
                <el-table-column
                  prop="freeFloatMarketCap"
                  label="自由流通市值(亿)"
                  sortable
                  :sort-method="sortFreeFloatMarketCap"
                  :formatter="formatMarketCap"
                />
                <el-table-column
                  prop="intervalChangePct"
                  label="区间涨幅"
                  sortable
                  :sort-method="sortIntervalChangePct"
                />
                <el-table-column
                  prop="intervalTurnoverYi"
                  label="区间成交(亿)"
                  sortable
                  :sort-method="sortIntervalTurnoverYi"
                  :formatter="formatMarketCap"
                />
                <el-table-column
                  prop="intervalNetYi"
                  label="区间净额(亿)"
                  sortable
                  :sort-method="sortIntervalNetYi"
                />
                <el-table-column
                  prop="netInflowOverTurnover"
                  label="净流入/区间成交"
                  sortable
                  :sort-method="sortNetInflowOverTurnover"
                  :formatter="formatRatio"
                />
                <el-table-column
                  prop="netInflowOverFloatCap"
                  label="净流入/流通市值"
                  sortable
                  :sort-method="sortNetInflowOverFloatCap"
                  :formatter="formatRatio"
                />
                <el-table-column
                  prop="netInflowOverChangePct"
                  label="净流入/区间涨幅"
                  sortable
                  :sort-method="sortNetInflowOverChangePct"
                  :formatter="formatRatio"
                />
              </el-table>
              <div v-if="!stocksLoading && !showIntervalStocks.length" class="empty-tip">
                {{ selectedDailySubPlatesForStocks.length ? '暂无符合条件的个股' : '请先在每日明细中勾选子板块' }}
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
      </div>
    </div>

    <el-dialog title="添加大板块" :visible.sync="addDialogVisible" width="400px">
      <el-form label-width="60px" size="small">
        <el-form-item label="名称" required>
          <el-input v-model="addForm.plateName" placeholder="如：医药" />
        </el-form-item>
        <el-form-item label="Code" required>
          <el-input v-model="addForm.plateId" placeholder="如：801045" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="handleAddPlate">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import stockApi from '@/api/stockApi';
import { BREAKPOINTS } from '@/utils/device';
import { applyCompareScores, avgMetric } from '@/utils/trendCompareScore';

const STORAGE_KEY = 'trend_analysis_custom_plates';

const DEFAULT_PLATES = [
  { plateId: '801660', plateName: '通信', isDefault: true },
  { plateId: '801001', plateName: '芯片', isDefault: true },
  { plateId: '801807', plateName: '算力', isDefault: true },
  { plateId: '801159', plateName: '机器人概念', isDefault: true }
];

function formatYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 5);
  return { dStart: formatYmd(start), dEnd: formatYmd(end) };
}

export default {
  name: 'TrendAnalysis',
  data() {
    const { dStart, dEnd } = defaultDateRange();
    return {
      isMobile: false,
      dStart,
      dEnd,
      customPlates: [],
      selectedPlates: [],
      selectedSubPlatesByParent: {},
      confirmedSubPlatesByParent: {},
      hasQueried: false,
      subPlatesLoaded: false,
      subPlatesLoading: false,
      exportLoading: false,
      pageLoading: false,
      subPlatesByParent: {},
      activeParentTab: '',
      intervalStocksRaw: [],
      dailyRows: [],
      activeDailyDates: [],
      activeTab: 'daily',
      tabLoading: false,
      stocksLoading: false,
      selectedDailySubPlateIds: [],
      onlyMainBoard: true,
      filterGt80Yi: true,
      filterLt80Yi: false,
      addDialogVisible: false,
      addForm: { plateName: '', plateId: '' },
      restoringSubPlateSelection: false,
      restoringDailySelection: false
    };
  },
  computed: {
    isMini() {
      return this.isMobile ? 'mini' : 'small';
    },
    plates() {
      return [...DEFAULT_PLATES, ...this.customPlates];
    },
    canExport() {
      return (
        this.hasQueried &&
        this.selectedPlates.length &&
        this.subPlatesLoaded &&
        !this.subPlatesLoading &&
        !this.exportLoading
      );
    },
    allSelectedSubPlates() {
      return this.flattenSubPlatesByParent(this.selectedSubPlatesByParent);
    },
    allConfirmedSubPlates() {
      return this.flattenSubPlatesByParent(this.confirmedSubPlatesByParent);
    },
    subPlateTableMaxHeight() {
      return this.isMobile ? 280 : 400;
    },
    drillTableMaxHeight() {
      return this.isMobile ? 280 : 400;
    },
    selectedSubPlateNames() {
      return this.allConfirmedSubPlates.map((item) => item.plateName).join('、');
    },
    showSubPlateColumn() {
      return this.allConfirmedSubPlates.length > 1;
    },
    showParentPlateColumn() {
      const parentIds = new Set(this.allConfirmedSubPlates.map((sp) => sp.parentPlateId));
      return parentIds.size > 1;
    },
    selectedDailySubPlatesForStocks() {
      const ids = new Set(this.selectedDailySubPlateIds);
      return this.allConfirmedSubPlates.filter((sp) => ids.has(sp.plateId));
    },
    showIntervalSubPlateColumn() {
      return this.selectedDailySubPlatesForStocks.length > 1;
    },
    showIntervalStocks() {
      let list = this.intervalStocksRaw || [];
      const hasFilters = this.onlyMainBoard || this.filterGt80Yi || this.filterLt80Yi;
      if (!hasFilters) return list;

      return list.filter((stock) => {
        const shortCode = String(stock.code || '').trim().split('.')[0];

        if (this.onlyMainBoard) {
          const isMainBoard = shortCode.startsWith('60') || shortCode.startsWith('00');
          if (!isMainBoard) return false;
        }

        const mv = stock.freeFloatMarketCap;
        if (this.filterGt80Yi && this.filterLt80Yi) {
          return true;
        }
        if (this.filterGt80Yi) {
          if (mv == null || mv <= 80) return false;
        }
        if (this.filterLt80Yi) {
          if (mv == null || mv >= 80) return false;
        }

        return true;
      });
    },
    groupedDailyRows() {
      const byDate = {};
      this.dailyRows.forEach((row) => {
        const dateKey = String(row.tradeDate || '');
        if (!dateKey) return;
        if (!byDate[dateKey]) byDate[dateKey] = [];
        byDate[dateKey].push({
          ...row,
          dailyRowKey: `${row.plateId}_${dateKey}`
        });
      });
      return Object.keys(byDate)
        .sort((a, b) => b.localeCompare(a))
        .map((tradeDate) => ({
          tradeDate,
          rows: byDate[tradeDate]
        }));
    }
  },
  mounted() {
    this.loadCustomPlates();
    if (this.plates[0]) {
      this.selectedPlates = [this.plates[0]];
      this.activeParentTab = this.plates[0].plateId;
    }
    this.updateMobile();
    window.addEventListener('resize', this.updateMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateMobile);
  },
  methods: {
    updateMobile() {
      this.isMobile = window.innerWidth <= BREAKPOINTS.MOBILE;
    },
    isPlateSelected(plate) {
      return this.selectedPlates.some((p) => p.plateId === plate.plateId);
    },
    getSubPlateTableRef(parentPlateId) {
      const ref = this.$refs[`subPlateTable_${parentPlateId}`];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    getDailyTableRef(tradeDate) {
      const ref = this.$refs[`dailyTable_${tradeDate}`];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    syncDailyCollapseActive() {
      this.activeDailyDates = this.groupedDailyRows.map((group) => group.tradeDate);
    },
    flattenSubPlatesByParent(map) {
      const result = [];
      Object.entries(map || {}).forEach(([parentId, rows]) => {
        const parent = this.selectedPlates.find((p) => p.plateId === parentId);
        (rows || []).forEach((sp) => {
          result.push({
            ...sp,
            parentPlateId: parentId,
            parentPlateName: parent?.plateName || parentId
          });
        });
      });
      return result;
    },
    reconcileSubPlateSelections() {
      const next = {};
      this.selectedPlates.forEach((parent) => {
        const rows = this.subPlatesByParent[parent.plateId] || [];
        const prev = this.selectedSubPlatesByParent[parent.plateId] || [];
        const prevIds = new Set(prev.map((sp) => sp.plateId));
        const matched = rows.filter((r) => prevIds.has(r.plateId));
        if (matched.length) {
          next[parent.plateId] = matched;
        }
      });
      this.selectedSubPlatesByParent = next;
    },
    restoreAllSubPlateSelections() {
      this.$nextTick(() => {
        this.restoringSubPlateSelection = true;
        this.selectedPlates.forEach((parent) => {
          const table = this.getSubPlateTableRef(parent.plateId);
          if (!table) return;
          table.clearSelection();
          const selected = this.selectedSubPlatesByParent[parent.plateId] || [];
          const rows = this.subPlatesByParent[parent.plateId] || [];
          selected.forEach((sp) => {
            const row = rows.find((r) => r.plateId === sp.plateId);
            if (row) table.toggleRowSelection(row, true);
          });
        });
        this.$nextTick(() => {
          this.restoringSubPlateSelection = false;
        });
      });
    },
    restoreDailySelections() {
      this.$nextTick(() => {
        this.restoringDailySelection = true;
        this.groupedDailyRows.forEach((group) => {
          const table = this.getDailyTableRef(group.tradeDate);
          if (!table) return;
          table.clearSelection();
          const selectedIds = new Set(this.selectedDailySubPlateIds);
          group.rows.forEach((row) => {
            if (selectedIds.has(row.plateId)) {
              table.toggleRowSelection(row, true);
            }
          });
        });
        this.$nextTick(() => {
          this.restoringDailySelection = false;
        });
      });
    },
    handleDailySelectionChange(group, rows) {
      if (this.restoringDailySelection) return;
      const groupPlateIds = new Set(group.rows.map((row) => row.plateId));
      const selectedInGroup = new Set(rows.map((row) => row.plateId));
      const next = new Set(this.selectedDailySubPlateIds);
      groupPlateIds.forEach((plateId) => {
        if (selectedInGroup.has(plateId)) next.add(plateId);
        else next.delete(plateId);
      });
      this.selectedDailySubPlateIds = [...next];
      this.restoreDailySelections();
    },
    clearCompareScoresInSubPlates() {
      Object.values(this.subPlatesByParent).forEach((rows) => {
        (rows || []).forEach((sp) => {
          this.$set(sp, 'compareScore', null);
        });
      });
    },
    applyCompareScoresToSelection(dailyRows) {
      const selectedIds = new Set(this.allConfirmedSubPlates.map((sp) => sp.plateId));
      if (!selectedIds.size) {
        this.clearCompareScoresInSubPlates();
        return dailyRows.map((row) => ({ ...row, compareScore: null }));
      }

      const comparePool = dailyRows
        .filter((row) => selectedIds.has(row.plateId))
        .map((row) => ({ ...row, compareScore: null }));
      applyCompareScores(comparePool);

      const compareByKey = new Map(
        comparePool.map((row) => [`${row.plateId}_${row.tradeDate}`, row.compareScore])
      );

      const avgByPlate = {};
      comparePool.forEach((row) => {
        if (row.compareScore == null) return;
        const pid = row.plateId;
        if (!avgByPlate[pid]) avgByPlate[pid] = [];
        avgByPlate[pid].push(row.compareScore);
      });

      Object.values(this.subPlatesByParent).forEach((rows) => {
        (rows || []).forEach((sp) => {
          this.$set(
            sp,
            'compareScore',
            selectedIds.has(sp.plateId) ? avgMetric(avgByPlate[sp.plateId] || []) : null
          );
        });
      });

      return dailyRows.map((row) => ({
        ...row,
        compareScore: selectedIds.has(row.plateId)
          ? compareByKey.get(`${row.plateId}_${row.tradeDate}`) ?? null
          : null
      }));
    },
    restoreSubPlateSelection(parentPlateId) {
      this.$nextTick(() => {
        const table = this.getSubPlateTableRef(parentPlateId);
        if (!table) return;
        this.restoringSubPlateSelection = true;
        table.clearSelection();
        const selected = this.selectedSubPlatesByParent[parentPlateId] || [];
        const rows = this.subPlatesByParent[parentPlateId] || [];
        selected.forEach((sp) => {
          const row = rows.find((r) => r.plateId === sp.plateId);
          if (row) table.toggleRowSelection(row, true);
        });
        this.$nextTick(() => {
          this.restoringSubPlateSelection = false;
        });
      });
    },
    handleParentTabClick() {
      if (this.activeParentTab) {
        this.restoreSubPlateSelection(this.activeParentTab);
      }
    },
    formatMetric(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-';
      const num = Number(cellValue);
      if (Number.isNaN(num)) return cellValue;
      return num.toFixed(4);
    },
    formatScore(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-';
      const num = Number(cellValue);
      if (Number.isNaN(num)) return cellValue;
      return num.toFixed(2);
    },
    formatMarketCap(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-';
      const num = Number(cellValue);
      if (Number.isNaN(num)) return cellValue;
      return num.toFixed(2);
    },
    formatRatio(row, column, cellValue) {
      if (cellValue == null || cellValue === '') return '-';
      const num = Number(cellValue);
      if (Number.isNaN(num)) return cellValue;
      return `${(num * 100).toFixed(2)}%`;
    },
    sortScoreAsc(a, b, key) {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return Number(av) - Number(bv);
    },
    sortDailyScore(a, b) {
      return this.sortScoreAsc(a, b, 'score');
    },
    sortDailyCompareScore(a, b) {
      return this.sortScoreAsc(a, b, 'compareScore');
    },
    parsePct(val) {
      if (val == null || val === '') return null;
      const num = parseFloat(String(val).replace('%', '').trim());
      return Number.isNaN(num) ? null : num;
    },
    sortIntervalChangePct(a, b) {
      const av = this.parsePct(a.intervalChangePct);
      const bv = this.parsePct(b.intervalChangePct);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av - bv;
    },
    sortIntervalNetYi(a, b) {
      return this.sortScoreAsc(a, b, 'intervalNetYi');
    },
    sortIntervalTurnoverYi(a, b) {
      return this.sortScoreAsc(a, b, 'intervalTurnoverYi');
    },
    sortFreeFloatMarketCap(a, b) {
      return this.sortScoreAsc(a, b, 'freeFloatMarketCap');
    },
    sortNetInflowOverTurnover(a, b) {
      return this.sortScoreAsc(a, b, 'netInflowOverTurnover');
    },
    sortNetInflowOverFloatCap(a, b) {
      return this.sortScoreAsc(a, b, 'netInflowOverFloatCap');
    },
    sortNetInflowOverChangePct(a, b) {
      return this.sortScoreAsc(a, b, 'netInflowOverChangePct');
    },
    safeRatio(numerator, denominator) {
      const num = Number(numerator);
      const den = Number(denominator);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
      return num / den;
    },
    attachIntervalStockDerived(stocks) {
      return (stocks || []).map((item) => {
        const net = item.intervalNetYi;
        const turnover = item.intervalTurnoverYi;
        const cap = item.freeFloatMarketCap;
        const pct = this.parsePct(item.intervalChangePct);
        return {
          ...item,
          netInflowOverTurnover: this.safeRatio(net, turnover),
          netInflowOverFloatCap: this.safeRatio(net, cap),
          netInflowOverChangePct: this.safeRatio(net, pct)
        };
      });
    },
    mergeIntervalStocks(stocks, subPlates) {
      const plates = subPlates || this.selectedDailySubPlatesForStocks;
      if (plates.length <= 1) {
        return stocks;
      }
      const plateOrder = plates.map((item) => item.plateName);
      const map = new Map();
      stocks.forEach((item) => {
        const key = String(item.code || '').trim() || String(item.name || '').trim();
        if (!key) return;
        const plateName = item.subPlateName || '';
        if (!map.has(key)) {
          map.set(key, {
            ...item,
            subPlateNames: plateName ? [plateName] : []
          });
          return;
        }
        const existing = map.get(key);
        if (plateName && !existing.subPlateNames.includes(plateName)) {
          existing.subPlateNames.push(plateName);
        }
      });
      return Array.from(map.values()).map((row) => {
        const names = row.subPlateNames.slice().sort((a, b) => {
          const ia = plateOrder.indexOf(a);
          const ib = plateOrder.indexOf(b);
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        const merged = { ...row };
        delete merged.subPlateNames;
        return {
          ...merged,
          subPlateName: names.join('、')
        };
      });
    },
    loadCustomPlates() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        this.customPlates = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(this.customPlates)) this.customPlates = [];
      } catch (e) {
        this.customPlates = [];
      }
    },
    saveCustomPlates() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.customPlates));
    },
    handleAddPlate() {
      const name = String(this.addForm.plateName || '').trim();
      const code = String(this.addForm.plateId || '').trim();
      if (!name || !code) {
        this.$message.warning('名称和 Code 均必填');
        return;
      }
      if (!/^80\d{4}$/.test(code)) {
        this.$message.warning('Code 格式应为 80xxxx');
        return;
      }
      if (this.plates.some((p) => p.plateId === code)) {
        this.$message.warning('该 Code 已存在');
        return;
      }
      this.customPlates.push({ plateId: code, plateName: name, isDefault: false });
      this.saveCustomPlates();
      this.addForm = { plateName: '', plateId: '' };
      this.addDialogVisible = false;
      this.$message.success('已添加');
    },
    handleRemovePlate(plate) {
      if (plate.isDefault) return;
      this.customPlates = this.customPlates.filter((p) => p.plateId !== plate.plateId);
      this.saveCustomPlates();
      this.selectedPlates = this.selectedPlates.filter((p) => p.plateId !== plate.plateId);
      this.$delete(this.subPlatesByParent, plate.plateId);
      this.$delete(this.selectedSubPlatesByParent, plate.plateId);
      this.$delete(this.confirmedSubPlatesByParent, plate.plateId);
      if (this.activeParentTab === plate.plateId) {
        this.activeParentTab = this.selectedPlates[0]?.plateId || '';
      }
      if (this.hasQueried) this.fetchSubPlates();
    },
    async handleQuery() {
      if (!this.dStart || !this.dEnd) {
        this.$message.warning('请选择日期');
        return;
      }
      if (!this.selectedPlates.length) {
        if (this.plates[0]) {
          this.selectedPlates = [this.plates[0]];
          this.activeParentTab = this.plates[0].plateId;
        } else {
          this.$message.warning('请先选择大板块');
          return;
        }
      }
      this.hasQueried = true;
      this.selectedSubPlatesByParent = {};
      this.confirmedSubPlatesByParent = {};
      this.intervalStocksRaw = [];
      this.dailyRows = [];
      this.selectedDailySubPlateIds = [];
      await this.fetchSubPlates(true);
    },
    handlePlateClick(plate) {
      const idx = this.selectedPlates.findIndex((p) => p.plateId === plate.plateId);
      if (idx >= 0) {
        if (this.selectedPlates.length > 1) {
          this.selectedPlates.splice(idx, 1);
          this.$delete(this.subPlatesByParent, plate.plateId);
          this.$delete(this.selectedSubPlatesByParent, plate.plateId);
          this.$delete(this.confirmedSubPlatesByParent, plate.plateId);
          if (this.activeParentTab === plate.plateId) {
            this.activeParentTab = this.selectedPlates[0]?.plateId || '';
          }
        }
      } else {
        this.selectedPlates.push(plate);
        this.activeParentTab = plate.plateId;
      }
      this.intervalStocksRaw = [];
      this.dailyRows = [];
      this.selectedDailySubPlateIds = [];
      this.confirmedSubPlatesByParent = {};
      if (this.hasQueried) {
        this.fetchSubPlates();
      }
    },
    clearAllSubPlateSelections() {
      this.selectedSubPlatesByParent = {};
      this.confirmedSubPlatesByParent = {};
      this.selectedPlates.forEach((parent) => {
        const table = this.getSubPlateTableRef(parent.plateId);
        if (table) table.clearSelection();
      });
      this.clearCompareScoresInSubPlates();
    },
    async fetchSubPlates(clearSelection = false) {
      if (!this.selectedPlates.length) return;
      this.subPlatesLoading = true;
      this.subPlatesLoaded = false;
      try {
        const tasks = this.selectedPlates.map(async (parent) => {
          const res = await stockApi.getTrendSubPlates({
            dStart: this.dStart,
            dEnd: this.dEnd,
            parentPlateId: parent.plateId
          });
          return { parent, subPlates: res.subPlates || [] };
        });
        const results = await Promise.all(tasks);
        const nextMap = {};
        results.forEach(({ parent, subPlates }) => {
          nextMap[parent.plateId] = subPlates;
        });
        this.subPlatesByParent = nextMap;
        if (!this.activeParentTab || !this.selectedPlates.some((p) => p.plateId === this.activeParentTab)) {
          this.activeParentTab = this.selectedPlates[0]?.plateId || '';
        }
        this.subPlatesLoaded = true;
        this.intervalStocksRaw = [];
        this.dailyRows = [];
        this.selectedDailySubPlateIds = [];
        this.confirmedSubPlatesByParent = {};
        if (clearSelection) {
          this.clearAllSubPlateSelections();
        } else {
          this.reconcileSubPlateSelections();
          this.restoreAllSubPlateSelections();
        }
      } catch (err) {
        this.$message.error(err.message || '查询失败');
        this.subPlatesByParent = {};
      } finally {
        this.subPlatesLoading = false;
      }
    },
    handleSubPlateRowClick(parent, row, column) {
      if (column && column.type === 'selection') return;
      const table = this.getSubPlateTableRef(parent.plateId);
      if (table) {
        table.toggleRowSelection(row);
      }
    },
    handleSubPlateSelectionChange(parent, rows) {
      if (this.restoringSubPlateSelection) return;
      this.$set(this.selectedSubPlatesByParent, parent.plateId, rows);
      this.clearCompareScoresInSubPlates();
    },
    handleConfirmSubPlates() {
      const next = {};
      Object.entries(this.selectedSubPlatesByParent).forEach(([parentId, rows]) => {
        if (rows && rows.length) next[parentId] = rows.slice();
      });
      this.confirmedSubPlatesByParent = next;
      if (!this.allConfirmedSubPlates.length) {
        this.intervalStocksRaw = [];
        this.dailyRows = [];
        this.selectedDailySubPlateIds = [];
        this.clearCompareScoresInSubPlates();
        return;
      }
      this.activeTab = 'daily';
      this.loadSubPlateDetails();
    },
    handleDrillTabClick(tab) {
      if (tab.name === 'stocks') {
        this.loadIntervalStocks();
      }
    },
    async enrichStocksMarketCap(stocks) {
      const codes = [...new Set(
        (stocks || [])
          .map((item) => String(item.code || '').trim())
          .filter(Boolean)
      )];
      if (!codes.length) return stocks;
      const res = await stockApi.getStocksFreeFloatMarketCap(codes);
      const capMap = res.marketCapByCode || {};
      return stocks.map((item) => {
        const shortCode = String(item.code || '').trim().split('.')[0];
        return {
          ...item,
          freeFloatMarketCap: capMap[shortCode] ?? null
        };
      });
    },
    async loadIntervalStocks() {
      const selected = this.selectedDailySubPlatesForStocks;
      if (!selected.length) {
        this.intervalStocksRaw = [];
        return;
      }
      this.stocksLoading = true;
      try {
        const stockTasks = selected.map(async (subPlate) => {
          const stocksRes = await stockApi.getTrendIntervalStocks({
            dStart: this.dStart,
            dEnd: this.dEnd,
            subPlateId: subPlate.plateId
          });
          return { subPlate, stocksRes };
        });
        const stockResults = await Promise.all(stockTasks);
        const stocks = [];
        stockResults.forEach(({ subPlate, stocksRes }) => {
          (stocksRes.stocks || []).forEach((item) => {
            stocks.push({ ...item, subPlateName: subPlate.plateName });
          });
        });
        const merged = this.mergeIntervalStocks(stocks, selected);
        const enriched = await this.enrichStocksMarketCap(merged);
        this.intervalStocksRaw = this.attachIntervalStockDerived(enriched);
      } catch (err) {
        this.$message.error(err.message || '加载区间个股失败');
        this.intervalStocksRaw = [];
      } finally {
        this.stocksLoading = false;
      }
    },
    async loadSubPlateDetails() {
      const selected = this.allConfirmedSubPlates;
      if (!selected.length) return;
      this.tabLoading = true;
      this.dailyRows = [];
      this.intervalStocksRaw = [];
      try {
        const byParent = {};
        selected.forEach((sp) => {
          if (!byParent[sp.parentPlateId]) {
            byParent[sp.parentPlateId] = { parentName: sp.parentPlateName, subPlates: [] };
          }
          byParent[sp.parentPlateId].subPlates.push(sp);
        });

        const dailyTasks = Object.entries(byParent).map(async ([parentId, group]) => {
          const dailyRes = await stockApi.getTrendDailyDetailsBatch({
            dStart: this.dStart,
            dEnd: this.dEnd,
            parentPlateId: parentId,
            parentPlateName: group.parentName,
            subPlateIds: group.subPlates.map((sp) => sp.plateId)
          });
          return { parentId, subPlates: group.subPlates, dailyRes };
        });

        const dailyResults = await Promise.all(dailyTasks);

        const daily = [];
        dailyResults.forEach(({ parentId, subPlates, dailyRes }) => {
          (dailyRes.rows || []).forEach((item) => {
            const sp = subPlates.find((s) => s.plateId === item.plateId);
            daily.push({
              ...item,
              plateName: item.plateName || sp?.plateName || '',
              parentPlateName: sp?.parentPlateName || item.parentPlateName || '',
              parentPlateId: parentId
            });
          });
        });

        daily.sort((a, b) => {
          const dateCmp = String(a.tradeDate).localeCompare(String(b.tradeDate));
          if (dateCmp !== 0) return dateCmp;
          return String(a.plateName || '').localeCompare(String(b.plateName || ''));
        });
        this.dailyRows = this.applyCompareScoresToSelection(daily);
        this.selectedDailySubPlateIds = [];
        this.syncDailyCollapseActive();
        this.restoreDailySelections();
      } catch (err) {
        this.$message.error(err.message || '加载失败');
      } finally {
        this.tabLoading = false;
      }
    },
    async handleExport() {
      if (!this.canExport) return;
      const parentPlates = this.selectedPlates.map((parent) => {
        const selection = this.selectedSubPlatesByParent[parent.plateId] || [];
        return {
          plateId: parent.plateId,
          plateName: parent.plateName,
          subPlateIds: selection.length
            ? selection.map((item) => item.plateId)
            : undefined
        };
      });
      this.exportLoading = true;
      try {
        const blob = await stockApi.exportTrendAnalysis({
          dStart: this.dStart,
          dEnd: this.dEnd,
          parentPlates
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const label = parentPlates.length === 1
          ? parentPlates[0].plateName || parentPlates[0].plateId
          : '多板块';
        a.href = url;
        a.download = `trend_${label}_${this.dStart}_${this.dEnd}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$message.error(err.message || '导出失败');
      } finally {
        this.exportLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.trend-analysis {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 16px;

  &--queried {
    height: 100%;
    overflow: hidden;
  }

  .filter-area {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    background: #fff;
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 12px;

    &--mobile {
      flex-direction: column;
      align-items: stretch;
    }

    .filter-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .filter-label {
      color: #606266;
      font-size: 14px;
      white-space: nowrap;
    }

    .filter-date {
      width: 150px;
    }
  }

  .stocks {
    flex-shrink: 0;
    background: #fff;
    padding: 10px 20px;
    border-radius: 4px;
    margin-bottom: 12px;

    .stocks-title {
      font-weight: bold;
      margin: 10px 0 15px 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 14px;
      color: #303133;
    }

    .stocks-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding-top: 5px;

      &--h5 {
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .stock-item {
        cursor: pointer;
        flex-shrink: 0;
      }

      .stock-tag {
        cursor: pointer;
      }
    }
  }

  .content-stack {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
  }

  .results-area,
  .drill-area {
    background: #fff;
    padding: 12px 16px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .results-area {
    flex: 0 0 auto;
  }

  .content-stack--with-drill {
    .drill-area {
      flex: 1 1 0;
      min-height: 0;
    }
  }

  .section-title {
    flex-shrink: 0;
    font-weight: bold;
    margin-bottom: 12px;
    font-size: 14px;
    color: #303133;

    &--with-action {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }

    .section-sub {
      font-weight: normal;
      color: #909399;
      font-size: 13px;
    }
  }

  .selection-tip {
    flex-shrink: 0;
    margin-top: 8px;
    font-size: 13px;
    color: #606266;
  }

  .panel-tabs--fixed {
    flex: 0 0 auto;

    ::v-deep {
      .el-tabs__content,
      .el-tab-pane {
        height: auto;
        overflow: visible;
      }
    }
  }

  .panel-tabs--flex {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ::v-deep {
      .el-tabs__header {
        flex-shrink: 0;
      }

      .el-tabs__content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      .el-tab-pane {
        height: 100%;
        overflow: hidden;
      }
    }
  }

  .sub-plates-table-wrap {
    max-height: 400px;
    overflow: hidden;

    @media (max-width: 768px) {
      max-height: 280px;
    }
  }

  .drill-stocks-table-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .drill-stocks-tab-body {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .drill-stocks-filter {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 10px;
  }

  .daily-selection-tip {
    flex-shrink: 0;
    margin-bottom: 8px;
    font-size: 13px;
    color: #606266;
  }

  .daily-tab-body {
    height: 100%;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .daily-groups-wrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .daily-collapse {
    border: none;

    ::v-deep {
      .el-collapse-item__header {
        font-weight: 600;
        font-size: 14px;
        color: #303133;
        height: 40px;
        line-height: 40px;
        padding-left: 4px;
        border-bottom: 1px solid #ebeef5;
      }

      .el-collapse-item__wrap {
        border-bottom: none;
      }

      .el-collapse-item__content {
        padding: 8px 0 12px;
      }
    }
  }

  .daily-table-wrap {
    overflow: hidden;
  }

  ::v-deep .even-table {
    .el-table__header-wrapper table,
    .el-table__body-wrapper table {
      table-layout: fixed;
      width: 100%;
    }

    .el-table__header th,
    .el-table__body td {
      text-align: center;
    }

    .cell {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-left: 4px;
      padding-right: 4px;
    }
  }

  ::v-deep .sub-plate-table {
    .el-table__body-wrapper {
      overflow-x: hidden;
    }
  }

  .empty-tip {
    text-align: center;
    color: #909399;
    padding: 16px;
  }

  @media screen and (max-width: 1023px) {
    padding: 10px;

    .filter-area,
    .stocks {
      margin-bottom: 10px;
    }

    .content-stack--with-drill {
      .drill-area {
        flex: 1 1 0;
      }
    }
  }
}
</style>
